import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ChannelBadge } from '@/features/transactions/components/ChannelBadge'
import { PriorityDot } from '@/features/alerts/components/queue/PriorityDot'
import { StatusIcon } from '@/features/alerts/components/queue/StatusIcon'
import { ScoreCell } from '@/features/alerts/components/queue/ScoreCell'
import { formatDateTime, formatNaira } from '@/lib/format'
import type { AlertSummary } from '@/features/alerts/types'
import { UnlinkButton } from './UnlinkButton'

interface Props {
  caseId: string
  alerts: AlertSummary[]
}

/**
 * AF-127 — table of every alert linked to a case.
 *
 * Each row is a compact summary (priority, alert#, scenario, score, customer,
 * amount, channel, status, created) and exposes an `UnlinkButton`. The unlink
 * button gates on the case's total alert count when ≥5 — see UnlinkButton.
 */
export function LinkedAlertsTable({ caseId, alerts }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Linked alerts ({alerts.length})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {alerts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-fg">
            No alerts are currently linked to this case.
          </p>
        ) : (
          <ul className="flex flex-col">
            {alerts.map((a) => (
              <li
                key={a.id}
                className="grid h-12 items-center gap-3 border-b border-border/60 px-4 text-sm last:border-b-0 grid-cols-[8px_minmax(0,7rem)_minmax(0,1fr)_minmax(0,4rem)_minmax(0,1fr)_minmax(0,7rem)_minmax(0,3rem)_minmax(0,1.5rem)_minmax(0,7rem)_minmax(0,5rem)]"
              >
                <PriorityDot priority={a.priority} />
                <Link
                  to={`/alerts/${a.id}`}
                  className="truncate font-mono text-xs tabular-nums text-muted-fg hover:text-fg"
                >
                  {a.alert_number}
                </Link>
                <Link
                  to={`/alerts/${a.id}`}
                  className="min-w-0 truncate hover:underline"
                  title={a.scenario_name}
                >
                  <span className="font-medium">{a.scenario_name}</span>
                </Link>
                <ScoreCell score={a.score} />
                <Link
                  to={`/customers/${a.customer.id}`}
                  className="min-w-0 truncate text-fg hover:underline"
                >
                  {a.customer.display_name}
                </Link>
                <span className="text-right font-mono tabular-nums text-xs">
                  {a.amount_minor !== null ? formatNaira(a.amount_minor) : '—'}
                </span>
                <ChannelBadge channel={a.channel} />
                <span className="flex justify-center">
                  <StatusIcon status={a.status} />
                </span>
                <span className="font-mono tabular-nums text-xs text-muted-fg">
                  {formatDateTime(a.created_at)}
                </span>
                <span className="flex items-center justify-end gap-1">
                  <UnlinkButton
                    caseId={caseId}
                    alertIds={[a.id]}
                    caseAlertCount={alerts.length}
                  />
                  <Link
                    to={`/alerts/${a.id}`}
                    className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
                    aria-label={`Open alert ${a.alert_number}`}
                  >
                    <ChevronRight size={14} aria-hidden />
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
